import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Heart, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { AuthContainer } from '@/components/shared';
import { ErrorState } from '@/components/shared/error-state';
import { LoadingState } from '@/components/shared/loading-state';
import { PartnerSwitchDialog } from '@/components/shared/partner-switch-dialog';
import { Button } from '@/components/ui/button';
import { useAcceptInvite } from '@/hooks/mutations/use-accept-invite';
import { useValidateInvite } from '@/hooks/queries/use-validate-invite';
import { useAuthStore, useInviteStore } from '@/stores';

export const Route = createFileRoute('/invite/$token')({
  component: InvitePage,
});

function InvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { setInviteToken } = useInviteStore();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    currentPartnerName: string;
    newPartnerName: string;
  } | null>(null);

  const { data, isLoading, isError, refetch } = useValidateInvite(token);

  const {
    mutate: acceptInvite,
    mutateAsync: acceptInviteAsync,
    isPending: isAcceptPending,
    isSuccess: isAcceptSuccess,
    isError: isAcceptError,
    data: acceptResponse,
  } = useAcceptInvite();

  // If user is authenticated, auto-accept invite
  useEffect(() => {
    if (isAuthenticated && data) {
      acceptInvite({ invite_token: token });
    }
  }, [isAuthenticated, data, token, acceptInvite]);

  // Handle accept invite response
  useEffect(() => {
    if (isAcceptSuccess && acceptResponse) {
      if (acceptResponse.status === 'requires_confirmation') {
        setConfirmationData({
          currentPartnerName: acceptResponse.current_partner_name,
          newPartnerName: acceptResponse.new_partner_name,
        });
        setShowConfirmDialog(true);
      } else {
        navigate({ to: '/home' });
      }
    }
  }, [isAcceptSuccess, acceptResponse, navigate]);

  const handleAcceptClick = () => {
    if (!data) return;

    setInviteToken(token, data.creator_name);
    navigate({ to: '/invite/onboarding' });
  };

  const handleConfirmSwitch = async () => {
    try {
      await acceptInviteAsync({
        invite_token: token,
        confirm: true,
      });
      setShowConfirmDialog(false);
      navigate({ to: '/home' });
    } catch {
      toast.error('Erro ao trocar de parceiro. Tente novamente.');
    }
  };

  const handleCancelSwitch = () => {
    setShowConfirmDialog(false);
    setConfirmationData(null);
    navigate({ to: '/home' });
  };

  if (isLoading) {
    return (
      <AuthContainer>
        <LoadingState message="Validando convite..." />
      </AuthContainer>
    );
  }

  if (isError) {
    return (
      <AuthContainer>
        <ErrorState
          title="Convite inválido"
          message="Este convite não existe ou já expirou."
          onRetry={() => refetch()}
        />
      </AuthContainer>
    );
  }

  // If authenticated and mutation is pending or processing
  if (isAuthenticated) {
    if (isAcceptPending) {
      return (
        <AuthContainer>
          <LoadingState message="Aceitando convite..." />
        </AuthContainer>
      );
    }

    if (isAcceptError) {
      return (
        <AuthContainer>
          <ErrorState
            title="Erro ao aceitar convite"
            message="Ocorreu um erro ao aceitar o convite. Tente novamente."
            onRetry={() => acceptInvite({ invite_token: token })}
          />
        </AuthContainer>
      );
    }
  }

  return (
    <AuthContainer>
      <div className="space-y-6 text-center">
        <div className="bg-primary/10 mx-auto flex h-16 w-16 items-center justify-center rounded-full">
          <Heart className="text-primary h-8 w-8" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            {data?.creator_name} te convidou!
          </h1>
          <p className="text-muted-foreground text-sm">
            Você foi convidado(a) para compartilhar listas de desejos e datas
            especiais no eumimo.
          </p>
        </div>

        <Button
          onClick={handleAcceptClick}
          className="w-full"
          size="lg"
          disabled={isAcceptPending}
        >
          {isAcceptPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Aceitar convite
        </Button>
      </div>

      {confirmationData && (
        <PartnerSwitchDialog
          open={showConfirmDialog}
          onOpenChange={setShowConfirmDialog}
          currentPartnerName={confirmationData.currentPartnerName}
          newPartnerName={confirmationData.newPartnerName}
          onConfirm={handleConfirmSwitch}
          onCancel={handleCancelSwitch}
          isLoading={isAcceptPending}
        />
      )}
    </AuthContainer>
  );
}
